import React, { useState } from 'react';
import moment from 'moment';
import { Key } from '@philips-internal/oswald-ui-constants';
import { classNames, isFunction, trigger } from '@philips-internal/oswald-ui-utils';
import { DatePickerMode } from '../date-picker-constants.js';
import { getCalendarTitleFormat, getGranularity } from '../date-picker-util.js';
function isSameDate(dateA, dateB, granularity = 'date') {
    return !!dateA && !!dateB && moment(dateA).isSame(dateB, granularity);
}
function isCurrent(date) {
    return isSameDate(moment().utc(), date);
}
function hasEvents(events = []) {
    return Boolean(events.length);
}
function isSelected(cellDate, currentDate, mode) {
    return isSameDate(currentDate, cellDate, getGranularity(mode));
}
function verifyThresholdDates(intervalThreshold, startDate, cellDate, rangeEndDate, setRangeEndDate) {
    if (intervalThreshold <= 0 || !startDate) {
        return true;
    }
    const intervalMinusCurrentDay = intervalThreshold - 1;
    const date = moment(cellDate);
    const maxEndDate = moment(startDate).add(intervalMinusCurrentDay, 'days');
    if (!rangeEndDate) {
        return date.isSameOrBefore(maxEndDate);
    }
    else {
        if (rangeEndDate > maxEndDate) {
            setRangeEndDate(maxEndDate);
        }
        return date.isSameOrBefore(maxEndDate)
            && date.isSameOrAfter(moment(rangeEndDate).subtract(intervalMinusCurrentDay, 'days'));
    }
}
function verifyIntervalDates(interval, copiedDate) {
    const date = moment(copiedDate);
    return (!interval.startDate || date.isSameOrAfter(interval.startDate, 'day'))
        && (!interval.endDate || date.isSameOrBefore(interval.endDate, 'day'));
}
function verifyDayInMonth(renderDate, currentDate) {
    if (!renderDate || !currentDate) {
        return false;
    }
    const firstDay = currentDate.clone().startOf('month');
    const lastDay = currentDate.clone().endOf('month');
    return renderDate.isBetween(firstDay, lastDay) || renderDate.isSame(firstDay, 'date');
}
function getDisabled(props) {
    const { enabled, intervalThreshold, item, loading, rangeEnabled, ranges = [], startDate, endDate } = props;
    if (!enabled) {
        return true;
    }
    const { active, date: cellDate } = item;
    const [rangeEndDate, setRangeEndDate] = useState(endDate);
    const isInRange = ranges.some(range => verifyIntervalDates(range, cellDate));
    const statusRange = !rangeEnabled === isInRange;
    const isInThreshold = verifyThresholdDates(intervalThreshold ?? 0, startDate, cellDate, rangeEndDate, setRangeEndDate);
    return !active || loading || statusRange || !isInThreshold;
}
function getItemBusyClass(busyPercentage) {
    if (busyPercentage === 100) {
        return 'item-busy-full';
    }
    if (busyPercentage > 75 && busyPercentage <= 99) {
        return 'item-busy-four-quarter';
    }
    if (busyPercentage > 50 && busyPercentage <= 75) {
        return 'item-busy-three-quarter';
    }
    if (busyPercentage > 25 && busyPercentage <= 50) {
        return 'item-busy-two-quarter';
    }
    if (busyPercentage >= 1 && busyPercentage <= 25) {
        return 'item-busy-one-quarter';
    }
    return '';
}
function getClassName(props, isDisabled) {
    const { currentDate, endDate, interval, intervalThreshold, item, loading, mode = DatePickerMode.MONTHLY, rangeEnabled, ranges = [], startDate, monthDate, } = props;
    const [rangeEndDate, setRangeEndDate] = useState(endDate);
    const { active, busyPercent, date: cellDate, isDateInInterval, events = [], specialDay = false } = item;
    const isInRange = ranges.some(range => verifyIntervalDates(range, cellDate));
    const statusRange = !rangeEnabled === isInRange;
    const isInThreshold = verifyThresholdDates(intervalThreshold ?? 0, startDate, cellDate, rangeEndDate, setRangeEndDate);
    const isActive = Boolean(active);
    const isFirstLimit = isSameDate(cellDate, startDate);
    const isLastLimit = isSameDate(cellDate, rangeEndDate || startDate);
    const isLimit = isFirstLimit || isLastLimit;
    const isHidden = interval && !active;
    const selected = interval
        ? (isDateInInterval || isLimit) && isInThreshold
        : isSelected(cellDate, currentDate, mode);
    const isBetweenMonth = mode === DatePickerMode.MONTHLY ? verifyDayInMonth(cellDate, monthDate) : true;
    const itemBusyFillClass = getItemBusyClass(Number(busyPercent));
    return classNames({
        'c-date-picker__item': true,
        'has-events': hasEvents(events) && isActive,
        'is-blackout': statusRange || !isInThreshold,
        'is-current': isCurrent(cellDate) && !loading && !statusRange,
        'is-disabled': isDisabled,
        'is-first-limit': isFirstLimit,
        'is-last-limit': isLastLimit,
        'is-limit': isLimit,
        'is-selected': selected && isActive && !loading,
        'is-special-day': Boolean(specialDay),
        'item-busy': isActive && Number(busyPercent) >= 1,
        [itemBusyFillClass]: isActive,
        'u-hidden': isHidden || !isBetweenMonth,
    });
}
function DatePickerCell(props) {
    const { mode = DatePickerMode.MONTHLY, item, onClick } = props;
    const isDisabled = getDisabled(props);
    const text = getCalendarTitleFormat(mode, item.date);
    const cellClassName = getClassName(props, isDisabled);
    const onKeyDown = (event) => {
        if (event.key === Key.ENTER && isFunction(onClick)) {
            onClick(item);
        }
    };
    return (React.createElement("div", { className: cellClassName, onClick: !isDisabled ? trigger(onClick, item) : undefined, onKeyDown: onKeyDown, role: "button", tabIndex: 0 },
        React.createElement("span", null, text)));
}
export { DatePickerCell, };
//# sourceMappingURL=date-picker-cell.js.map